import { memo, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { isSvpCategory } from '../../../modules/helpers';
import {
  getAssetDisplayTitle,
  getAssetMainImage,
  getAssetPromoUrl,
  isCurrentlyLive,
} from '../../../shared/helpers/assetHelpers';
import { Badge } from '../../Asset/shared/Badge/Badge';
import { UpcomingLiveBadge } from '../../Asset/shared/UpcomingLiveBadge/UpcomingLiveBadge';
import { AssetButtons } from '../../Splash/SplashButtons/AssetButtons';
import { MixedSlider } from '../MixedSlider/MixedSlider';
import {
  getCategoryBackgroundImage,
  getCategoryPromoUrl,
} from '../../../shared/helpers/categoryHelpers';
import { getAssetLabels } from '../../Splash/AssetSplash/getAssetLabels';
import { getCategoryLabels } from '../../Splash/CategorySplash/getCategoryLabels';
import { useVendor } from '../../VendorPovider/VendorPovider';
import { useAppSelector } from '../../../modules/hooks';
import { selectVendorCategories } from '../../../modules/categories';
import { CategoryButtons } from '../../Splash/SplashButtons/CategoryButtons';

import { CarouselSplash } from './CarouselSplash/CarouselSplash';
import { Carousel } from './Carousel/Carousel';

import type { Vendor } from '../../../types';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

import * as styles from './DesktopCarousel.module.scss';

const getPreviewUrl = (item: SvpAssetAdditional | SvpCategoryAdditional) => {
  return isSvpCategory(item)
    ? getCategoryPromoUrl(item)
    : getAssetPromoUrl(item);
};

type DesktopCarouselProps = {
  currentIndex: number;
  onCurrentIndexChange: (
    value: number | ((prevState: number) => number)
  ) => void;
  isFirstComponent: boolean;
  items: (SvpCategoryAdditional | SvpAssetAdditional)[];
  goForward?: () => void;
  vendor: Vendor;
  serverTime: number;
  trackButtonSplashClick: (buttonType: string) => void;
  pageComponentIndex: number;
  onThumbnailHover: (index?: number) => void;
};

const DesktopCarouselBase = forwardRef<HTMLDivElement, DesktopCarouselProps>(
  (
    {
      currentIndex,
      onCurrentIndexChange,
      isFirstComponent,
      items,
      goForward,
      serverTime,
      trackButtonSplashClick,
      pageComponentIndex,
      onThumbnailHover,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const { vendor } = useVendor();
    const categories = useAppSelector((state) =>
      selectVendorCategories(state, vendor)
    );

    return (
      <>
        <div
          className={classNames(styles.desktopCarouselWrapper, {
            [styles.isPulledUp]: isFirstComponent,
          })}
        >
          <Carousel
            currentIndex={currentIndex}
            onCurrentIndexChange={onCurrentIndexChange}
            ref={ref}
            className={styles.carousel}
          >
            {items.map((item, index) => {
              const commonProps = {
                hidden: index !== currentIndex,
                previewUrl:
                  index === currentIndex ? getPreviewUrl(item) : undefined,
                lazy: !(isFirstComponent && index === 0),
              };

              if (isSvpCategory(item)) {
                return (
                  <CarouselSplash
                    {...commonProps}
                    key={`category:${item.id}`}
                    imageUrl={getCategoryBackgroundImage(item)!}
                    onPreviewEnded={goForward}
                    title={item.title}
                    description={item.additional.description}
                    labels={getCategoryLabels(item, categories[item.parentId])}
                    buttons={
                      <CategoryButtons
                        trackSplashClick={trackButtonSplashClick}
                        category={item}
                      />
                    }
                  />
                );
              }

              return (
                <CarouselSplash
                  {...commonProps}
                  key={`asset:${item.id}`}
                  imageUrl={getAssetMainImage(item)}
                  onPreviewEnded={goForward}
                  title={getAssetDisplayTitle(item)}
                  description={item.description}
                  labels={getAssetLabels(item)}
                  buttons={
                    <AssetButtons
                      trackSplashClick={trackButtonSplashClick}
                      asset={item}
                    />
                  }
                  badge={
                    <>
                      {isCurrentlyLive(item, serverTime) && (
                        <Badge kind="live" label={t('Live')} />
                      )}
                      <UpcomingLiveBadge asset={item} />
                    </>
                  }
                />
              );
            })}
          </Carousel>
          <div className={styles.mixedSlider}>
            <MixedSlider
              pageComponentIndex={pageComponentIndex}
              onHover={onThumbnailHover}
              currentIndex={currentIndex}
              items={items}
              trackElementClick={trackButtonSplashClick}
            ></MixedSlider>
          </div>
        </div>
      </>
    );
  }
);
DesktopCarouselBase.displayName = 'DesktopCarouselBase';

export const DesktopCarousel = memo(DesktopCarouselBase);
